// Rejoue la file d'attente hors-ligne (lib/offline.ts) dès que le réseau
// revient. Chaque écriture porte une référence choisie par l'utilisateur
// (ou générée automatiquement) avant l'envoi : la rejouer ne crée donc
// jamais de doublon côté serveur (cf. `_replayable` dans
// api/routes/_shared.py — idempotence déjà en place côté API).
//
// Une requête qui échoue pour une vraie raison métier (stock insuffisant,
// référence inconnue, champ invalide...) n'est JAMAIS réessayée
// automatiquement : elle est marquée « en erreur » pour revue manuelle
// dans la page Synchronisation, conformément au choix retenu (acceptation
// automatique + alerte en cas d'incohérence).
import { useEffect, useState } from 'react';
import { api, ApiError } from './api';
import { listQueue, markQueueItem, removeQueueItem, QueueItem } from './offline';

type Listener = () => void;
const listeners = new Set<Listener>();
function notify() {
  listeners.forEach((l) => l());
}

let syncing = false;

export async function syncNow(): Promise<{ synced: number; failed: number }> {
  if (syncing || !navigator.onLine) return { synced: 0, failed: 0 };
  syncing = true;
  let synced = 0;
  let failed = 0;
  try {
    const items = (await listQueue()).filter((i) => i.status === 'pending');
    for (const item of items) {
      try {
        await api(item.path, { method: 'POST', body: item.body });
        await removeQueueItem(item.id);
        synced++;
      } catch (e) {
        if (!navigator.onLine) break; // reparti hors-ligne en cours de route : on s'arrête, on réessaiera plus tard
        if (e instanceof ApiError) {
          await markQueueItem(item.id, { status: 'error', attempts: item.attempts + 1, lastError: e.message });
          failed++;
        } else {
          break; // échec réseau inattendu : on s'arrête là, la suite sera retentée au prochain passage
        }
      }
    }
  } finally {
    syncing = false;
    notify();
  }
  return { synced, failed };
}

export async function retryItem(id: string): Promise<void> {
  await markQueueItem(id, { status: 'pending', lastError: undefined });
  notify();
  await syncNow();
}

export async function discardItem(id: string): Promise<void> {
  await removeQueueItem(id);
  notify();
}

export async function listQueueItems(): Promise<QueueItem[]> {
  return listQueue();
}

let wired = false;
function wire() {
  if (wired) return;
  wired = true;
  window.addEventListener('online', () => {
    notify();
    syncNow();
  });
  window.addEventListener('offline', notify);
  // Filet de sécurité : certains réseaux mobiles (terrain, zones faible
  // couverture) ne déclenchent pas toujours l'événement 'online' de façon
  // fiable au retour du signal.
  setInterval(() => {
    if (navigator.onLine) syncNow();
  }, 60_000);
  if (navigator.onLine) syncNow();
}

export function useOfflineStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [errors, setErrors] = useState(0);

  useEffect(() => {
    wire();
    let mounted = true;
    const refresh = () => {
      setOnline(navigator.onLine);
      listQueue().then((items) => {
        if (!mounted) return;
        setPending(items.filter((i) => i.status === 'pending').length);
        setErrors(items.filter((i) => i.status === 'error').length);
      });
    };
    refresh();
    listeners.add(refresh);
    window.addEventListener('online', refresh);
    window.addEventListener('offline', refresh);
    const t = setInterval(refresh, 15_000);
    return () => {
      mounted = false;
      listeners.delete(refresh);
      window.removeEventListener('online', refresh);
      window.removeEventListener('offline', refresh);
      clearInterval(t);
    };
  }, []);

  return { online, pending, errors };
}
